import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import RadioButtons from '@codegouvfr/react-dsfr/RadioButtons';
import clsx from 'clsx';
import { type ComponentPropsWithoutRef, useEffect } from 'react';
import AppRequiredInput from 'src/components/_app/AppRequired/AppRequiredInput';
import type { UseForm } from 'src/hooks/useForm';
import type { ZodObject, z } from 'zod';
import './AppRadioButtons.scss';

type AppRadioButtonsProps<T extends ZodObject, U extends UseForm<T>> = Pick<
  ComponentPropsWithoutRef<typeof RadioButtons>,
  'legend' | 'options' | 'state' | 'stateRelatedMessage' | 'disabled'
> & {
  inputForm: U;
  inputKey: keyof NoInfer<z.infer<U['schema']>>;
  inputPathFromKey?: (string | number)[];
  whenValid?: string;
  required?: boolean;
  colSm?: 2 | 3 | 4 | 6 | 12;
};

function AppRadioButtons<T extends ZodObject>(
  props: AppRadioButtonsProps<T, UseForm<T>>
) {
  const {
    inputKey,
    inputPathFromKey,
    inputForm,
    whenValid,
    state,
    stateRelatedMessage,
    colSm,
    ...radioButtonsProps
  } = props;

  const isRichRadio =
    radioButtonsProps.options.find(
      (options) => options.illustration !== undefined
    ) !== undefined;

  useEffect(() => {
    if (
      radioButtonsProps.options.length === 1 &&
      !radioButtonsProps.options[0].nativeInputProps.checked
    ) {
      // @ts-expect-error TS2345
      radioButtonsProps.options[0].nativeInputProps?.onChange?.();
    }
  }, [radioButtonsProps.options]);

  return (
    <RadioButtons
      {...radioButtonsProps}
      legend={
        radioButtonsProps.legend && (
          <>
            {radioButtonsProps.legend}
            {radioButtonsProps.required && <AppRequiredInput />}
          </>
        )
      }
      state={state ?? inputForm.messageType(inputKey, inputPathFromKey)}
      stateRelatedMessage={
        stateRelatedMessage ??
        inputForm.message(inputKey, inputPathFromKey, whenValid)
      }
      classes={{
        inputGroup: cx('fr-col-12', {
          'fr-col-sm-6': colSm === 6,
          'fr-col-sm-4': colSm === 4,
          'fr-py-0': !isRichRadio,
          'fr-my-0': isRichRadio
        }),
        content: cx('fr-grid-row', 'fr-grid-row--gutters', 'fr-mx-0'),
        root: clsx(
          cx('fr-px-0', 'fr-my-0'),
          !isRichRadio ? 'maestro-radio-group-inline' : undefined
        ),
        legend: cx('fr-col-12', 'fr-mx-0')
      }}
    />
  );
}

export default AppRadioButtons;
